// ============================================================================
// OBT F3 — İnceleme medya galerisi (ACL'li blob + Object URL temizliği)
// ============================================================================
// Fotoğraf, ACL'li uçtan blob olarak alınır (ham storage yolu/URL kullanılmaz).
// `loadPhoto(photoId, {signal})` bir Promise<{objectUrl}> döndürmelidir; böylece
// aynı galeri hem cevap medyası hem öneri medyası için kullanılabilir. Object
// URL unmount/değişimde revoke edilir. Yüklenemeyen foto formu ÇÖKERTMEZ.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';

function ReviewPhotoThumb({ photoId, loadPhoto, alt }) {
  const [state, setState] = useState({ loading: true, url: null, failed: false });
  const urlRef = useRef(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ loading: true, url: null, failed: false });
    Promise.resolve(loadPhoto(photoId, { signal: controller.signal }))
      .then(({ objectUrl }) => {
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        urlRef.current = objectUrl;
        setState({ loading: false, url: objectUrl, failed: false });
      })
      .catch((e) => {
        if (!active || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setState({ loading: false, url: null, failed: true });
      });
    return () => {
      active = false;
      controller.abort();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [photoId, loadPhoto]);

  if (state.loading) {
    return (
      <div
        className="flex h-24 w-24 items-center justify-center rounded-md bg-gray-100"
        role="status"
      >
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />
        <span className="sr-only">Fotoğraf yükleniyor</span>
      </div>
    );
  }
  if (state.failed || !state.url) {
    return (
      <div
        className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md bg-gray-100 text-gray-400"
        role="img"
        aria-label="Fotoğraf görüntülenemedi"
      >
        <ImageOff className="h-4 w-4" aria-hidden="true" />
        <span className="text-[10px]">Görüntülenemedi</span>
      </div>
    );
  }
  return (
    <img
      src={state.url}
      alt={alt}
      className="h-24 w-24 rounded-md object-cover ring-1 ring-inset ring-gray-200"
    />
  );
}

/**
 * @param {object} props
 * @param {Array<string>} props.photoIds
 * @param {(photoId: string, opts: {signal: AbortSignal}) => Promise<{objectUrl: string}>} props.loadPhoto
 * @param {string} [props.emptyText]
 * @param {string} [props.altPrefix]
 */
export default function ReviewMediaGallery({
  photoIds,
  loadPhoto,
  emptyText = 'Bu cevaba bağlı fotoğraf yok.',
  altPrefix = 'Cevap fotoğrafı',
}) {
  const ids = Array.isArray(photoIds) ? photoIds : [];
  if (ids.length === 0) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {ids.map((pid, i) => (
        <ReviewPhotoThumb
          key={pid}
          photoId={pid}
          loadPhoto={loadPhoto}
          alt={`${altPrefix} ${i + 1}`}
        />
      ))}
    </div>
  );
}
